import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  MobileStepper,
  Paper,
  Alert,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip
} from '@mui/material'
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import GridViewIcon from '@mui/icons-material/GridView'
import SlideshowIcon from '@mui/icons-material/Slideshow'
import MenuIcon from '@mui/icons-material/Menu'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import VisualizationsFab from '../components/navigation/VisualizationsFab'
import { VisualizationSlide } from '../components/visualizations/VisualizationSlide'
import { visualizationSlides } from '../fixtures/visualizations/slide-config'

type ViewMode = 'slideshow' | 'grid'

const Visualizations: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const theme = useTheme()
  const [activeStep, setActiveStep] = useState(0)
  const [viewMode, setViewMode] = useState<ViewMode>('slideshow')
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  
  const maxSteps = visualizationSlides.length

  const handleNext = () => {
    setActiveStep((prevActiveStep) => 
      prevActiveStep < maxSteps - 1 ? prevActiveStep + 1 : 0
    )
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => 
      prevActiveStep > 0 ? prevActiveStep - 1 : maxSteps - 1
    )
  }

  const handleStepClick = (step: number) => {
    setActiveStep(step)
    setViewMode('slideshow')
    setDrawerOpen(false)
  }

  const handleToggleFullscreen = () => {
    setIsFullscreen(!isFullscreen)
  }

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (viewMode === 'slideshow') {
        if (event.key === 'ArrowLeft') {
          handleBack()
        } else if (event.key === 'ArrowRight') {
          handleNext()
        } else if (event.key === 'Escape' && isFullscreen) {
          setIsFullscreen(false)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [viewMode, isFullscreen, activeStep])

  if (!account.user) {
    return (
      <Container>
        <Box sx={{ mt: 4 }}>
          <Alert severity="warning">Please log in to view visualizations</Alert>
        </Box>
      </Container>
    )
  }

  const currentSlide = visualizationSlides[activeStep]

  return (
    <Container maxWidth={isFullscreen ? false : "xl"} sx={{ py: isFullscreen ? 0 : 4 }}>
      {!isFullscreen && (
        <>
          {/* Header */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h4" component="h1">
              Data Visualizations
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={() => setDrawerOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Button
                variant={viewMode === 'slideshow' ? 'contained' : 'outlined'}
                startIcon={<SlideshowIcon />}
                onClick={() => setViewMode('slideshow')}
              >
                Slideshow
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                startIcon={<GridViewIcon />}
                onClick={() => setViewMode('grid')}
              >
                Grid View
              </Button>
            </Box>
          </Box>

          {/* Customer/Job Info */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Customer: <strong>{account.customer?.displayName || account.customer?.name}</strong> | 
              Job: <strong>{account.job?.displayName || account.job?.name}</strong>
            </Typography>
          </Box>
        </>
      )}

      {/* Main Content */}
      {viewMode === 'slideshow' ? (
        <Box>
          {/* Current Slide */}
          <VisualizationSlide 
            slide={currentSlide}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
          />
          
          {/* Navigation Controls */}
          {!isFullscreen && (
            <MobileStepper
              variant="dots"
              steps={maxSteps}
              position="static"
              activeStep={activeStep}
              sx={{ mt: 2, bgcolor: 'transparent' }}
              nextButton={
                <Button
                  size="small"
                  onClick={handleNext}
                  endIcon={<KeyboardArrowRight />}
                >
                  Next
                </Button>
              }
              backButton={
                <Button
                  size="small"
                  onClick={handleBack}
                  startIcon={<KeyboardArrowLeft />}
                >
                  Back
                </Button>
              }
            />
          )}

          {/* Slide Counter */}
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Slide {activeStep + 1} of {maxSteps}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Use arrow keys to navigate
            </Typography>
          </Box>
        </Box>
      ) : (
        /* Grid View */
        <Grid container spacing={3}>
          {visualizationSlides.map((slide, index) => (
            <Grid item xs={12} md={6} lg={4} key={slide.id}>
              <Card>
                <CardActionArea onClick={() => handleStepClick(index)}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Chip 
                        label={`Slide ${index + 1}`} 
                        size="small" 
                        color="primary"
                        sx={{ mr: 1 }}
                      />
                      <Chip 
                        label={slide.chartType} 
                        size="small" 
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="h6" component="h3" gutterBottom>
                      {slide.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {slide.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Table: {slide.tableName}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Slide Navigation Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      >
        <Box sx={{ width: 300, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Slide Navigation
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <List>
            {visualizationSlides.map((slide, index) => (
              <ListItem key={slide.id} disablePadding>
                <ListItemButton
                  selected={activeStep === index && viewMode === 'slideshow'}
                  onClick={() => handleStepClick(index)}
                >
                  <ListItemText
                    primary={`${index + 1}. ${slide.title}`}
                    secondary={slide.chartType}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
      <VisualizationsFab />
    </Container>
  )
}

export default Visualizations